import { useId, useRef } from "react";
import { cn } from "@/lib/utils";
import { Button } from "../button";
import { Icon, type IconName } from "../icon";

export type UploadVariant = "photo" | "file";

export interface UploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /** Visual/content variant. Defaults to "photo". */
  variant?: UploadVariant;
  /** Native file input accept hint, e.g. "image/png,image/jpeg". */
  accept?: string;
  /** Display-only accepted formats text. Does not validate selected files. */
  acceptedFormatsLabel?: string;
  /** Display-only max size text. Does not validate selected files. */
  maxSizeLabel?: string;
  /** Allows selecting more than one file. */
  multiple?: boolean;
  /** Disables the select button and file input. */
  disabled?: boolean;
  /** Overrides the select button label. */
  buttonLabel?: string;
  /** Overrides the full guidance text. */
  guidanceLabel?: string;
  /** Called when files are selected from the native file picker. */
  onFilesSelected?: (files: File[]) => void;
}

type UploadDefaults = {
  icon: IconName;
  accept: string;
  acceptedFormatsLabel: string;
  buttonLabel: string;
};

const uploadDefaults: Record<UploadVariant, UploadDefaults> = {
  photo: {
    icon: "image-regular",
    accept: "image/png,image/jpeg",
    acceptedFormatsLabel: "PNG, JPG",
    buttonLabel: "Select Image",
  },
  file: {
    icon: "upload",
    accept: "application/pdf,text/plain",
    acceptedFormatsLabel: "PDF, TXT",
    buttonLabel: "Select File",
  },
};

function Upload({
  variant = "photo",
  accept,
  acceptedFormatsLabel,
  maxSizeLabel = "5 MB",
  multiple = false,
  disabled = false,
  buttonLabel,
  guidanceLabel,
  onFilesSelected,
  className,
  ...props
}: UploadProps) {
  const inputId = useId();
  const guidanceId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const defaults = uploadDefaults[variant];
  const resolvedAcceptedFormatsLabel =
    acceptedFormatsLabel ?? defaults.acceptedFormatsLabel;
  const resolvedButtonLabel = buttonLabel ?? defaults.buttonLabel;
  const resolvedAccept = accept ?? defaults.accept;
  const resolvedGuidanceLabel =
    guidanceLabel ??
    `Upload ${resolvedAcceptedFormatsLabel} up to ${maxSizeLabel}`;

  function handleSelectClick() {
    inputRef.current?.click();
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files ?? []);

    if (files.length > 0) {
      onFilesSelected?.(files);
    }

    event.currentTarget.value = "";
  }

  return (
    <div
      data-slot="upload"
      data-variant={variant}
      data-disabled={disabled || undefined}
      className={cn(
        "flex",
        "w-[480px]",
        "max-w-full",
        "relative",
        "flex-col",
        "items-center",
        "justify-center",
        "gap-(--spacing-24)",
        "rounded-(--radius-sm)",
        "bg-surface-container-lowest",
        "px-(--spacing-16)",
        "py-(--spacing-24)",
        disabled && "opacity-50",
        className,
      )}
      {...props}
    >
      <UploadStroke />

      <span data-slot="upload-icon" className="text-text-subtext">
        <Icon name={defaults.icon} size="lg" tone="inherit" aria-hidden />
      </span>

      <p
        id={guidanceId}
        className={cn(
          "m-0",
          "text-center",
          "font-(family-name:--font-family-label)",
          "font-(number:--font-weight-medium)",
          "text-(length:--font-size-body-medium)",
          "leading-(--font-line-height-20)",
          "text-text-subtext",
        )}
      >
        {resolvedGuidanceLabel}
      </p>

      <Button
        variant="outline"
        size="md"
        disabled={disabled}
        onClick={handleSelectClick}
        type="button"
        aria-describedby={guidanceId}
      >
        {resolvedButtonLabel}
      </Button>

      <input
        ref={inputRef}
        id={inputId}
        className="sr-only"
        type="file"
        accept={resolvedAccept}
        multiple={multiple}
        disabled={disabled}
        tabIndex={-1}
        aria-label={resolvedButtonLabel}
        onChange={handleInputChange}
      />
    </div>
  );
}

function UploadStroke() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 size-full"
      fill="none"
      preserveAspectRatio="none"
      viewBox="0 0 480 188"
    >
      <rect
        className="stroke-outline-subtle"
        x="0.5"
        y="0.5"
        width="479"
        height="187"
        rx="4"
        strokeDasharray="8 6"
        strokeLinecap="butt"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export { Upload };
